<template>
  <div>
    <Spinner v-if="isPageDataLoading" />
    <TabbedBox v-if="!isPageDataLoading">
      <template v-slot:tablist>
        <template v-for="(taskType, index) of activeTaskTypes">
          <Tab :first="index == 0" :tabid="`tab-tasktype-${taskType.id}`" :key="taskType.id">
            <i class="far fa-list-ol"></i> {{taskType.name}}
          </Tab>
        </template>
        <Tab tabid="tab-calendar" @show="onCalendarTabShown">
          <i class="far fa-calendar"></i> Calendar
        </Tab>
        <Tab tabid="tab-map">
          <i class="far fa-map"></i> Map View
        </Tab>
        <Tab tabid="tab-orders">
          <i class="far fa-shopping-cart"></i> {{orderListTabTitle}}
        </Tab>
      </template>
      <template v-slot:tabcontent>
        <template v-for="(taskType, index) of activeTaskTypes">
          <TabPage :first="index == 0" :tabid="`tab-tasktype-${taskType.id}`" :key="taskType.id">
            <TaskDataTable ref="taskDataTable" :useQueryData="true" :taskTypeIdFilter="taskType.id">
              <template v-slot:tools>
                <ActionButton type="scan" v-if="userHasUpdatePermission(PermissionTypes.TaskPermissionTypes.task) && isScanTaskItemEnabled" class="mr-2" @click="showScanTaskItemsDespatched"> Scan Despatched</ActionButton>
                <ActionButton type="new" v-if="userHasCreatePermission(PermissionTypes.TaskPermissionTypes.task)" sizeClass="btn" @click="createTaskClicked(taskType.id)"> New {{taskType.name}}</ActionButton>
              </template>
              <template v-slot:title>
                <ContentTitle>{{taskType.name}} Tasks</ContentTitle>
              </template>
            </TaskDataTable>
          </TabPage>
        </template>

        <TabPage tabid="tab-calendar">
          <Spinner v-if="pageDataLoading" />
          <div v-show="!pageDataLoading" class="pb-1">
            <div class="row no-gutters mb-1">
              <div class="col-6">
                <ContentTitle>Task Calendar</ContentTitle>
              </div>
              <div class="col-6">
                <div class="text-left">
                  <div v-show="taskBackgroundUpdating">
                    <Spinner class="inline" small /> <span class="text-muted">Syncing tasks...</span>
                  </div>
                  <span class="text-danger" v-show="taskUpdateError">{{taskUpdateError}}</span>
                </div>
                <div class="text-right">
                  <FilterInput :filterOptions="calendarFilterOptions" v-model="calendarFilter" class="float-right" title="Calendar Filter" />
                  <div class="float-right">
                    <ActionButton type="date" class="btn-medium mr-2" @click="setDateToday"> Today</ActionButton>
                  </div>
                </div>
              </div>
            </div>
            <CalendarMonthSelector ref="monthSelector" v-model="currentDay" />
          </div>

          <div class="task-manager-calendar-container" v-if="!pageDataLoading">
            <FullCalendar :options="calendarOptions" ref="calendar">
              <template v-slot:eventContent="arg">
                <CalendarDayGridTaskView :title="arg.event.title" :timeText="arg.timeText" :task="arg.event.extendedProps" @dblclick.native="displayEditTask(arg.event.extendedProps)" />
              </template>

              <template v-slot:resourceLabelContent ="arg">
                {{arg.resource.title}}
              </template>
            </FullCalendar>
          </div>
        </TabPage>

        <TabPage tabid="tab-map">
          <div v-show="!pageDataLoading" class="pb-1">
            <div class="row no-gutters mb-1">
              <div class="col-6">
                <ContentTitle>Task Map View</ContentTitle>
              </div>
              <div class="col-6">
                <div class="text-left">
                  <div v-show="taskBackgroundUpdating">
                    <Spinner class="inline" small /> <span class="text-muted">Syncing tasks...</span>
                  </div>
                  <span class="text-danger" v-show="taskUpdateError">{{taskUpdateError}}</span>
                </div>
                <div class="text-right">
                  <div class="float-right">
                    <FilterInput :filterOptions="mapViewFilterOptions" v-model="mapViewFilter" title="Map Orders Filter" />
                  </div>
                  <div class="float-right">
                    <ActionButton type="date" class="btn-medium mr-2" @click="setDateToday"> Today</ActionButton>
                  </div>
                  <div class="float-right">
                    <a href="#" class="btn btn-default btn-medium mr-2" @click.prevent="displayMapLegendPopover"><i class="far fa-map-marker-question" /></a>
                  </div>
                  <MapLegendPopover ref="mapLegendPopover" />
                </div>
              </div>
            </div>
            <CalendarDaySelector ref="daySelector" v-model="currentDay" :eventsList="calendarOptions.events" />
          </div>

          <div class="task-manager-map-container">
            <TaskManagerMapView ref="mapView" :currentDay="currentDay" :draggableMarkersEnabled="true" :mapOrderFilter="mapViewFilter" />
          </div>
        </TabPage>

        <TabPage tabid="tab-orders">
          <TaskDespatchOrderDataTable :openNewTab="true" :selectionEnabled="false">
            <template v-slot:title>
              <ContentTitle>{{orderListTabTitle}}</ContentTitle>
            </template>
          </TaskDespatchOrderDataTable>
        </TabPage>
      </template>
    </TabbedBox>
  </div>
</template>

<style lang="scss">
@import "@/modules/tasks/scss/calendar.scss";
@import "@/modules/tasks/scss/manager.scss";
</style>

<style scoped>
.task-manager-calendar-container,
.task-manager-map-container {
  height: 90vh;
}
.task-order-spacer {
  height: 8px;
}
.task-order-list {
  padding-right: 4px;
  max-height: 70vh;
  overflow-x: none;
  overflow-y: auto;
}
.task-order-list h4 {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import FullCalendar from '@fullcalendar/vue'
import ResourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import DayGridPlugin from '@fullcalendar/daygrid'
import InteractionPlugin from '@fullcalendar/interaction'

import pdfMixin from '@/mixins/pdf'
import EventBus from '@/utils/eventBus'
import Utils from '@/utils/utils'
import ColourMixin from '@/mixins/colour'
import DoubleClickMixin from '@/mixins/doubleClick'
import TaskManagerMixin from '@/modules/tasks/mixins/taskManagerMixin'

import FilterInput from '@/components/filter/filterInput'
import TaskManagerMapView from '@/modules/tasks/components/taskManagerMapView'
import CalendarDaySelector from '@/modules/tasks/components/calendarDaySelector'
import CalendarMonthSelector from '@/modules/tasks/components/calendarMonthSelector'
import CalendarResourceTaskView from '@/modules/tasks/components/calendarResourceTaskView'
import CalendarDayGridTaskView from '@/modules/tasks/components/calendarDayGridTaskView'
import TaskDialog from '@/modules/tasks/views/taskDialog'
import TaskManagerTaskPanel from '@/modules/tasks/components/taskManagerTaskPanel'
import TaskDataTable from '@/modules/tasks/components/taskDataTable'
import MapLegendPopover from '@/modules/tasks/components/mapLegendPopover'
import OrderLegendPopover from '@/modules/tasks/components/orderLegendPopover'
import ScanTaskItemDespatchedDialog from '@/modules/tasks/views/scanTaskItemDespatchedDialog'
import TaskDespatchOrderDataTable from '@/modules/tasks/components/taskDespatchOrderDataTable'
import TaskNewDialog from '@/modules/tasks/views/taskNewDialog'

import { TaskStatusTypes, AccountSettingKeys } from '@/store/types'

export default {
  name: 'TaskManager',
  mixins: [ColourMixin, DoubleClickMixin, pdfMixin, TaskManagerMixin],
  components: {
    FilterInput,
    FullCalendar,
    TaskManagerMapView,
    CalendarDaySelector,
    CalendarMonthSelector,
    CalendarResourceTaskView,
    CalendarDayGridTaskView,
    TaskDataTable,
    TaskManagerTaskPanel,
    MapLegendPopover,
    OrderLegendPopover,
    TaskDespatchOrderDataTable
  },
  props: {
    isDeliveryManager: Boolean,
    isPickupManager: Boolean
  },
  watch: {
    currentDay: function() {
      this.$refs.calendar.getApi().gotoDate(moment(this.currentDay).local().format('YYYY-MM-DD'))

      // Request events for the time range if day is outside the current range
      this.debouncedTaskUpdate()
    },
    visibleTaskList: function() {
      this.buildFullCalendarEventList()
    },
    calendarFilter: function() {
      this.buildFullCalendarEventList()
    },
    searchText: function() {
      this.scheduleListLoading = true
      this.debouncedScheduleSearch()
    },
    scheduleFilter: function() {
      this.scheduleListLoading = true
      this.debouncedScheduleSearch()
    }
  },
  computed: {
    taskManagerTitle() {
      return 'Tasks'
    },
    orderListTabTitle() {
      return 'Order List'
    },
    calendarFilterOptions() {
      var filterOptions = []

      filterOptions.push({
        name: 'Task Types',
        field: 'taskTypeId',
        columnIndex: 0,
        options: this.activeTaskTypes.map(taskType => {
          return {
              name: taskType.name,
              value: taskType.id,
              colour: taskType.highlightColour
            }
        })
      })

      filterOptions.push({
        name: 'Resources',
        field: 'resources',
        columnIndex: 0,
        options: this.calendarOptions.resources.map(resource => {
          return {
              name: resource.title,
              value: resource.id,
              colour: resource.extendedProps.highlightColour
            }
        })
      })

      let taskLabels = this.$store.getters['label/getLabelsByType']('task')
      filterOptions.push({
        name: 'Task Label',
        field: 'labels',
        columnIndex: 0,
        options: taskLabels.reverse().map(labelInfo => {
          return {
            name: labelInfo.name,
            value: labelInfo.id,
            colour: labelInfo.colour
          }
        })
      })

      filterOptions.push({
        name: 'Users',
        field: 'users',
        columnIndex: 1,
        options: this.calendarOptions.users.map(user => {
          return {
              name: user.title,
              value: user.id,
              colour: user.extendedProps.highlightColour
            }
        })
      })

      return filterOptions
    },
    mapViewFilterOptions() {
      var mapFilterOptions = []

      let deliveryTagLabels = this.$store.getters['label/getLabelsByType']('contactAddressDelivery')
      mapFilterOptions.push({
        name: 'Order Delivery Tag',
        field: 'deliveryTag',
        options: deliveryTagLabels.map(labelInfo => {
          return {
            name: labelInfo.name,
            value: labelInfo.name,
            colour: labelInfo.colour
          }
        })
      })
      
      return mapFilterOptions
    },
    isScanTaskItemEnabled() {      
      return this.accountSettingGetValueBoolean(AccountSettingKeys.taskDespatchScanTaskItemsEnabled, false)
    },
    activeTaskTypes() {
      return this.taskTypeList.filter(taskType => taskType.isActive)
    }
  },
  data() {
    let self = this

    return {
      currentDay: moment().local().toDate(),
      orderDespatchScheduleError: '',
      orderDespatchScheduleList: [],
      orderDespatchSchedulePageCount: 0,
      orderDespatchScheduleOffset: 0,
      orderDespatchSchedulePageLength: 50,
      orderDespatchScheduleCurrentPage: 0,
      orderDespatchScheduleCurDraw: 0,
      orderDespatchScheduleRecvDraw: 0,

      taskBackgroundUpdating: false,
      taskUpdateError: '',

      scheduleListLoading: false,
      searchText: '',
      scheduleFilter: [],

      taskTypeList: [],

      visibleTaskList: [],
      resourceList: [],

      calendarFilter: [],
      mapViewFilter: [],

      draggableOrderList: null,
      draggableItemList: null,

      calendarOptions: {
        // License Key for FullCalendar - Purchased 11/11/2020
        schedulerLicenseKey: '0417191928-fcs-1605073591',

        plugins: [DayGridPlugin, InteractionPlugin],
        initialView: 'dayGridMonth',
        initialDate: moment().local().format('YYYY-MM-DD'),
        allDaySlot: false,
        headerToolbar: false,
        droppable: false,   // Default to non-editable & set via permissions
        editable: false,
        fixedWeekCount: false,
        height: '100%',
        expandRows: true,
        resources: [],
        users: [],
        slotLabelInterval: "00:15",   // 15min intervals. Looks busy, but requested by KC 29/06/21
        slotDuration: "00:15",
        slotMinTime: '07:00:00',
        slotMaxTime: '17:00:00',
        nowIndicator: true,
        businessHours: {
          startTime: '7:30',
          endTime: '16:00',
        },
        events: [],
        dateClick: function(info) {
          // FullCalendar doesn't support double click, so have to implement our own double click detection
          if(self.isClickDoubleClick()) {
            if(!self.userHasCreatePermission(self.PermissionTypes.TaskPermissionTypes.task))
              return

            let startTime = moment(info.dateStr).local()

            self.displayNewTask(null, startTime, null)
          }
        },
        eventDrop: (info) => {
          if(info.event.extendedProps.id) {
            var updateData = { scheduleStartDate: moment(info.event.start).toISOString(), scheduleEndDate: moment(info.event.end).toISOString() }
            
            // Resource updates
            if(info.newResource || info.oldResource) {
              updateData.resources = []
              if(info.newResource)
                updateData.resources.push(parseInt(info.newResource.id))

              if(_.isArray(info.event.extendedProps.resourceIds)) {
                info.event.extendedProps.resourceIds.forEach(existingResourceId => {
                  if(info.oldResource && parseInt(info.oldResource.id) == existingResourceId) {
                    return
                  }
                  updateData.resources.push(existingResourceId)
                })
              }
            }

            self.updateTask(parseInt(info.event.extendedProps.id), updateData)
            .then(() => { })
            .catch(err => {
              info.revert()
            })
          }
        },
        eventClassNames: (arg) => {
          return self.classForTask(arg.event.extendedProps)
        }
      },
      currentDisplayIndex: 0
    }
  },
  created() {
    this.debouncedTaskUpdate = _.debounce(() => {
      this.requestTaskList()
      .then(() => { })
      .catch(err => console.error(err))
    }, 250)
  },
  mounted() {
    EventBus.on(EventBus.EventTypes.permissionsUpdated, this.permissionsUpdated)
    
    // Setup permissions for the calendar
    this.calendarOptions.droppable = this.userHasCreatePermission(this.PermissionTypes.TaskPermissionTypes.task)
    this.calendarOptions.editable = this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task)
    
    // Load all the data required for the task manager
    this.pageLoadData(Promise.all([
      this.requestAvailabilityShifts(),
      this.requestResourceList(),
      this.requestUserList(),
      this.requestTaskTypeList(),
      this.requestTaskList(),
      this.requestFilterLabels()
    ]))
    .then(() => {
      this.$nextTick(() => {
        this.$refs.calendar.getApi().updateSize()
      })
    })
    .catch(err => this.handlePageLoadError(err))

    this.subscribeEndpoint('/tasks/taskmanager', () => {
      this.debouncedTaskUpdate()
    })
  },
  beforeRouteLeave(to, from, next) {
    next()

    // Reload tasks
    this.debouncedTaskUpdate()
  },
  beforeDestroy() {
    EventBus.off(EventBus.EventTypes.permissionsUpdated, this.permissionsUpdated)
    this.unsubscribeEndpoint('/tasks/taskmanager')
  },

  methods: {
    // Called when permissions updated/loaded
    permissionsUpdated() {
      // Setup permissions for the calendar
      this.calendarOptions.droppable = this.userHasCreatePermission(this.PermissionTypes.TaskPermissionTypes.task)
      this.calendarOptions.editable = this.userHasUpdatePermission(this.PermissionTypes.TaskPermissionTypes.task)
    },

    // Load the global availability shifts
    requestAvailabilityShifts() {
      return new Promise(async (resolve, reject) => {
        this.$apiService.adminService.getAvailabilityShiftList()
        .then(response => {
          let shiftList = response.data
          let paddingOffsetSeconds = 1.5*60*60    // Show 1.5 hours above/below the shift
          var dayMin = null
          var dayMax = null

          shiftList = shiftList.filter(shift => shift.isTaskShift)

          shiftList.forEach(shift => {
            let startTime = moment(shift.shiftStartTime, 'HH:mm:ss').diff(moment().startOf('day'), 'seconds')
            let endTime = moment(shift.shiftEndTime, 'HH:mm:ss').diff(moment().startOf('day'), 'seconds')

            dayMin = Math.min(dayMin || startTime, startTime)
            dayMax = Math.max(dayMax || endTime, endTime)
          })

          if(dayMin && dayMax) {
            this.calendarOptions.businessHours.startTime = moment().local().startOf('day').add(dayMin, 'seconds').format('HH:mm')
            this.calendarOptions.businessHours.endTime = moment().local().startOf('day').add(dayMax, 'seconds').format('HH:mm')

            dayMin = Math.max(dayMin-paddingOffsetSeconds, 0)
            dayMax = Math.min(dayMax+paddingOffsetSeconds, 24*60*60-1)

            this.calendarOptions.slotMinTime = moment().local().startOf('day').add(dayMin, 'seconds').format('HH:mm:ss')
            this.calendarOptions.slotMaxTime = moment().local().startOf('day').add(dayMax, 'seconds').format('HH:mm:ss')
          }

          return resolve()
        })
        .catch(err => {
          return reject(err)
        })
      })
    },

    // Get list of resources to display in grid-resource view
    requestResourceList() {
      return new Promise(async (resolve, reject) => {

        this.$apiService.tasksService.getResourceList()
        .then(response => {
          this.resourceList = response.data

          this.calendarOptions.resources = this.resourceList
          .filter(resource => resource.isActive)
          .map(resource => {
            return {
              id: resource.id,
              title: resource.name,
              extendedProps: {
                type: resource.type,
                highlightColour: resource.highlightColour,
              }
            }
          })

          return resolve()
        })
        .catch(err => {
          this.orderDespatchScheduleError = 'Error loading panel schedule'

          return reject(err)
        })
      })
    },
    // Get list of users to display in grid-resource view
    requestUserList() {
      return new Promise(async (resolve, reject) => {

        this.$apiService.userService.getUserList()
        .then(response => {
          this.userList = response.data.filter(user => user.isActive && user.isTaskResource)

          this.calendarOptions.users = this.userList
          .map(user => {
            return {
              id: user.id,
              title: user.name,
              extendedProps: {
                highlightColour: user.highlightColour,
              }
            }
          })

          return resolve()
        })
        .catch(err => {
          this.orderDespatchScheduleError = 'Error loading panel schedule'

          return reject(err)
        })
      })
    },
    // Get list of task types
    requestTaskTypeList() {
      return new Promise(async (resolve, reject) => {

        this.$apiService.tasksService.getTaskTypeList()
        .then(response => {
          this.taskTypeList = response.data

          return resolve()
        })
        .catch(err => {
          this.orderDespatchScheduleError = 'Error loading task type list'

          return reject(err)
        })
      })
    },

    // Get the list of tasks for the current date range
    requestTaskList() {
      return new Promise(async (resolve, reject) => {
        var resourceViewDayRange = 15     // 15 days ahead/behind the current date

        // Get tasks within a date range to reduce bandwidth
        // Resource view needs task X days ahead/behind the current date
        // Calendar view needs tasks for the current month
        let calendarStartRange = moment(this.currentDay).local().startOf('month')
        let calendarEndRange = moment(this.currentDay).local().endOf('month')
        let resourceStartRange = moment(this.currentDay).local().subtract(resourceViewDayRange, 'days').startOf('day')
        let resourceEndRange = moment(this.currentDay).local().add(resourceViewDayRange, 'days').endOf('day')

        let visibleRangeStartDate = moment.min(calendarStartRange, resourceStartRange)
        let visibleRangeEndDate = moment.max(calendarEndRange, resourceEndRange)

        var taskParams = {
          filter: {
            scheduleStartDate: {
              gte: visibleRangeStartDate.toDate().toISOString(),
              lt: visibleRangeEndDate.toDate().toISOString(),
            },
            includeResources: 1,
            includeUsers: 1,
            includeTaskItems: 1,
            includeLabels: 1
          }
        }

        this.$apiService.tasksService.getTaskList(taskParams)
        .then(response => {
          this.visibleTaskList = response.data

          return resolve()
        })
        .catch(err => {
          return reject(err)
        })
      })
    },

    // Get the label info for the filter popovers used by the task manager
    requestFilterLabels() {
      return new Promise(async (resolve, reject) => {
        try {
          let taskLabels = await this.$apiService.coreService.getLabelsForType('task')
          let deliveryTagLabels = await this.$apiService.coreService.getLabelsForType('contactAddressDelivery')

          this.$store.commit({
            type: 'label/updateLabels',
            labelType: 'task',
            labelList: taskLabels.data
          })
          this.$store.commit({
            type: 'label/updateLabels',
            labelType: 'contactAddressDelivery',
            labelList: deliveryTagLabels.data
          })

          return resolve()
        } catch(err) {
          return reject(err)
        }
      })      
    },

    // Set the current date to today
    setDateToday() {
      this.currentDay = moment().local().toDate()
    },


    displayEditTask(task, options = {}) {
      this.$router.push(`/tasks/${task.id}`)
    },

    // Display a new task dialog via 'New Task' button or double clicking a time on the calendar
    displayNewTask(resourceInfo = null, startDate = null, highlightColour = null) {
      if(startDate == null)
        startDate = moment()

      // If the task is dropped onto the month-view calendar / double clicked, the start time will be 12am, so default to start of the availability shift times
      if(startDate.format('HH:mm:ss') == '00:00:00') {
        var startTime = moment(this.calendarOptions.businessHours.startTime, 'HH:mm:ss')
        startDate.set({
          hour: startTime.hour(),
          minute: startTime.minute(),
          second: startTime.second()
        })
      }

      this.displayModal(TaskDialog, {
        isCreateMode: true,
      }, {
        scheduleStartDate: moment(startDate).toDate().toISOString(),
        scheduleEndDate: moment(startDate).add(2, 'hours').toDate().toISOString(),
        highlightColour: highlightColour ? highlightColour : '#808080',
        resources: resourceInfo ? [resourceInfo] : [],
      })
      .then(taskId => {
        if(taskId) {
          this.successToast('Task Added')

          // Refresh tasks & the schedule
          if(this.$refs.mapView)
            this.$refs.mapView.refreshMapData()
        }
      })
    },

    findOrderInSchedule(orderId) {
      for(var group of this.orderDespatchScheduleList) {
        let order = group.itemList.find(scheduleOrder => scheduleOrder.type == 'order' && scheduleOrder.orderId == orderId)
        if(order)
          return order
      }
      return null
    },
    findTaskInSchedule(taskId) {
      for(var group of this.orderDespatchScheduleList) {
        let task = group.itemList.find(scheduleItem => scheduleItem.type == 'task' && scheduleItem.taskId == taskId)
        if(task)
          return task
      }
      return null
    },

    // Find a target task in the resource view with a date/resource
    // Returns an object of the task from visibleTaskList
    // Returns null if no task found at the date & resource
    findExistingTaskForResourceView(date, resourceId) {
      return this.visibleTaskList.find(task => {
        if(!task.resources)
          return false
        // Needs to have matching resource
        if(task.resources.find(resource => resource.id == resourceId) == null)
          return false

        // Date needs to be within existing task date/time range
        if(moment(task.scheduleStartDate).isSameOrBefore(date) && moment(task.scheduleEndDate).isAfter(date))
          return true
        return false
      })
    },

    // Run a background task update
    // Used for calendar update functions
    // Returns a promise
    updateTask(taskId, updateData) {
      return new Promise(async (resolve, reject) => {
        try {

          this.taskBackgroundUpdating = true
          this.taskUpdateError = ''

          await this.$apiService.tasksService.updateTask(taskId, updateData)

          this.taskBackgroundUpdating = false
          this.taskUpdateError = ''

          // Refresh task list to update selector/calendars/etc
          this.debouncedTaskUpdate()

          return resolve()
        } catch(err) {
          // Display network error
          this.taskBackgroundUpdating = false
          this.taskUpdateError = 'Error updating task'
          
          return reject(err)
        }
      })
    },

    // Check if a task is editable
    // Note: The entire calendar is readonly if permissions don't have task update, so we can ignore that check
    isTaskEditable(task) {
      if(!task)
        return false
      if(task.status == TaskStatusTypes.completed || task.status == TaskStatusTypes.cancelled)
        return false
      return true
    },

    displayTaskSheet(id, options = {}) {
      var params = $.param(options)
      this.displayReportPDF(`/tasks/${id}/runsheet?${params}`)
    },

    displayMapLegendPopover(evt) {
      this.$refs.mapLegendPopover.displayFromElement(evt.target)
    },
    displayOrderLegendPopover(evt) {
      this.$refs.orderLegendPopover.displayFromElement(evt.target)
    },

    buildFullCalendarEventList() {
      var resourceIdFilter = this.calendarFilter
        .filter(filter => filter.field == 'resources')
        .map(filter => filter.value)

      var userIdFilter = this.calendarFilter
        .filter(filter => filter.field == 'users')
        .map(filter => filter.value)

      var taskTypeIdFilter = this.calendarFilter
        .filter(filter => filter.field == 'taskTypeId')
        .map(filter => filter.value)

      var labelIdFilter = this.calendarFilter
        .filter(filter => filter.field == 'labels')
        .map(filter => filter.value)

      // Convert to FullCalendar events
      this.calendarOptions.events = this.visibleTaskList
      .filter(task => {
        if(resourceIdFilter.length > 0) {
          let taskResourceIds = task.resources.map(resource => resource.id)
          let intersect = _.intersection(resourceIdFilter, taskResourceIds)

          if(intersect.length == 0)
            return false
        }
        if(userIdFilter.length > 0) {
          let taskUserIds = task.users.map(user => user.id)
          let intersect = _.intersection(userIdFilter, taskUserIds)

          if(intersect.length == 0)
            return false
        }
        if(taskTypeIdFilter.length > 0) {
          if(!taskTypeIdFilter.includes(task.taskTypeId))
            return false
        }
        if(labelIdFilter.length > 0) {
          let taskLabelIds = task.labels.map(label => label.id)
          let intersect = _.intersection(labelIdFilter, taskLabelIds)

          if(intersect.length == 0)
            return false
        }
        return true
      })
      .map(task => {
        return {
          display: 'block',
          title: task.name,
          start: moment(task.scheduleStartDate).local().toDate(),
          end: moment(task.scheduleEndDate).local().toDate(),
          allDay : false,
          editable: this.isTaskEditable(task),
          resourceIds: task.resources.map(resource => resource.id),
          borderColor: this.borderColourForTask(task),  
          backgroundColor: this.backgroundColourForTask(task),
          textColor: this.colourForTask(task),
          extendedProps: {
            id: task.id,
            sequenceNumber: task.sequenceNumber,
            description: task.description,
            status: task.status,
            highlightColour: task.highlightColour,
            labels: task.labels,
            taskItems: task.taskItems,
            resourceIds: task.resources.map(resource => resource.id),
            userIds: task.users.map(user => user.id),
            primaryUserId: task.primaryUserId,
            labels: task.labels
          }
        }
      })
    },
    onCalendarTabShown() {
      this.$nextTick(() => {
        if(this.$refs.calendar) {
          this.$refs.calendar.getApi().changeView('dayGridMonth')
          this.$refs.calendar.getApi().updateSize()
        }
      })
    },
    showScanTaskItemsDespatched() {
      this.displayModal(ScanTaskItemDespatchedDialog)
      .then((success) => {
        if(success) {
          this.successToast('Tasks Despatched')
          this.$refs.taskDataTable.reload()
        }
      })
    },
    createTaskClicked(taskTypeId) {
      this.displayModal(TaskNewDialog, {
        taskTypeId: taskTypeId
      })
      .then((newTaskId) => {
        if(newTaskId) {
          this.$router.push(`/tasks/${newTaskId}`)          
        }
      })
    }
  }
}
</script>
